/* eslint-disable prettier/prettier */

import { apiService } from './api.service';
import { ApiResponse } from '../types';

//
// -------------------------------------------
// TYPES
// -------------------------------------------
//

export type CoachingHoursStatus = 'pending' | 'approved' | 'rejected';

export interface CoachingHoursFile {
	id: string;
	filename: string;
	bloburl: string;
	mimetype: string;
	filesize: number;
}

export interface CoachingHoursLog {
	id: string;

	learnerid: string;
	learnerfullname: string;

	coachid?: string | null;

	programid?: string | null;
	programtitle?: string | null;

	sessiondate: string;

	starttime: string;
	endtime: string;

	durationminutes: number;

	status: CoachingHoursStatus;

	reviewedby?: string | null;
	reviewedbyname?: string | null;

	reviewedat?: string | null;
	reviewernotes?: string | null;

	file?: CoachingHoursFile | null;

	createdat: string;
	updatedat: string;
}

export interface CoachingHoursListResponse {
	logs: CoachingHoursLog[];

	total: number;

	page: number;
	limit: number;

	totalhours: number;
	pendinghours: number;
}

//
// -------------------------------------------
// REQUEST TYPES
// -------------------------------------------
//

export interface CreateCoachingHoursPayload {
	learnerfullname: string;

	sessiondate: string;

	starttime: string;
	endtime: string;

	learnerid?: string;
	programid?: string;

	fileid?: string;
}

export interface UpdateCoachingHoursPayload {
	learnerfullname?: string;

	sessiondate?: string;

	starttime?: string;
	endtime?: string;

	coachid?: string | null;
	programid?: string | null;

	fileid?: string | null;
}

export interface ReviewCoachingHoursPayload {
	status: 'approved' | 'rejected';
	
	reviewernotes?: string;
}

export interface CoachingHoursFilters {
	learnerid?: string;
	// coachid?: undefined;
	programid?: string;

	status?: CoachingHoursStatus;

	fromdate?: string;
	todate?: string;

	page?: number;
	limit?: number;
}

export interface UploadFileResponse {
	fileid: string;
	bloburl: string;
	filename: string;
}

//
// -------------------------------------------
// SERVICE
// -------------------------------------------
//

class CoachingHoursService {
	//
	// Upload coaching hours file
	//
	async uploadFile(
		file: File,
		onUploadProgress?: (progress: number) => void
	): Promise<ApiResponse<UploadFileResponse>> {
		try {
			const formData = new FormData();

			formData.append('file', file);

			return await apiService.post<UploadFileResponse>(
				'/coaching-hours/upload',
				formData,
				{
					headers: {
						'Content-Type': 'multipart/form-data',
					},

					// Upload Progress
					//
					onUploadProgress: progressEvent => {
						const total = progressEvent.total || 1;

						const percent = Math.round(
							(progressEvent.loaded * 100) / total
						);

						onUploadProgress?.(percent);
					},
				}
			);
		} catch (error) {
			console.error('Error uploading coaching hours file:', error);

			throw error;
		}
	}

	//
	// Create coaching hours log
	//
	async createLog(
		payload: CreateCoachingHoursPayload
	): Promise<ApiResponse<CoachingHoursLog>> {
		try {
			return await apiService.post<CoachingHoursLog>(
				'/coaching-hours',
				payload
			);
		} catch (error) {
			console.error('Error creating coaching hours log:', error);

			throw error;
		}
	}

	//
	// Get coaching hours logs
	//
	async getLogs(
		filters?: CoachingHoursFilters
	): Promise<ApiResponse<CoachingHoursListResponse>> {
		try {
			return await apiService.get<CoachingHoursListResponse>(
				'/coaching-hours',
				filters
			);
		} catch (error) {
			console.error('Error fetching coaching hours logs:', error);

			throw error;
		}
	}

	//
	// Get single coaching hours log
	//
	async getLogById(logId: string): Promise<ApiResponse<CoachingHoursLog>> {
		try {
			return await apiService.get<CoachingHoursLog>(
				`/coaching-hours/${logId}`
			);
		} catch (error) {
			console.error('Error fetching coaching hours log:', error);

			throw error;
		}
	}

	//
	// Update coaching hours log
	//
	async updateLog(
		logId: string,
		payload: UpdateCoachingHoursPayload
	): Promise<ApiResponse<CoachingHoursLog>> {
		try {
			return await apiService.put<CoachingHoursLog>(
				`/coaching-hours/${logId}`,
				payload
			);
		} catch (error) {
			console.error('Error updating coaching hours log:', error);

			throw error;
		}
	}

	//
	// Review coaching hours log
	//
	async reviewLog(
		logId: string,
		payload: ReviewCoachingHoursPayload
	): Promise<ApiResponse<CoachingHoursLog>> {
		try {
			return await apiService.patch<CoachingHoursLog>(
				`/coaching-hours/${logId}/review`,
				payload
			);
		} catch (error) {
			console.error('Error reviewing coaching hours log:', error);

			throw error;
		}
	}

	//
	// Delete coaching hours log
	//
	async deleteLog(logId: string): Promise<ApiResponse<void>> {
		try {
			return await apiService.delete<void>(`/coaching-hours/${logId}`);
		} catch (error) {
			console.error('Error deleting coaching hours log:', error);

			throw error;
		}
	}
}

//
// -------------------------------------------
// EXPORT INSTANCE
// -------------------------------------------
//

export const coachingHoursService = new CoachingHoursService();
